export const BLEED = 12;

const RED = '#ff3b3b';
const BLUE = '#4a9eff';

// Dyno-chart / HUD frame. A thin technical border with slightly clipped
// (angular) top corners, a row of RPM tick marks along the top edge with a few
// taller "redline" ticks toward the top-right, and two faint gently-rising dyno
// curves (blue torque, red power) hugging the bottom inside edge. A dim
// "RPM x1000" label sits top-left. All decoration stays on the border / bleed /
// bottom edge so the title, date and Going button in the centre stay clear.
export default function RedlineFrame({ w, h, radius = 12, accent }) {
  const W = w + BLEED * 2;
  const H = h + BLEED * 2;
  const x = BLEED;
  const y = BLEED;
  const x2 = x + w;
  const y2 = y + h;
  const c = 12; // top-corner chamfer

  // Border: square bottom corners, chamfered (angular) top corners.
  const border =
    `M ${x} ${y2} L ${x} ${y + c} L ${x + c} ${y}` +
    ` L ${x2 - c} ${y} L ${x2} ${y + c} L ${x2} ${y2} Z`;

  // RPM tick scale along the top edge. Major ticks every 5th, taller redline
  // ticks for the last few positions toward the top-right.
  const tickCount = 22;
  const tickGap = (w - 28) / (tickCount - 1);
  const tickX0 = x + 14;
  const redlineFrom = tickCount - 5; // last 5 ticks are the redline band
  const ticks = Array.from({ length: tickCount }, (_, i) => {
    const tx = tickX0 + i * tickGap;
    const isRedline = i >= redlineFrom;
    const isMajor = i % 5 === 0;
    const len = isRedline ? 9 : isMajor ? 6 : 3.5;
    return { tx, len, isRedline, isMajor };
  });

  // Two gently-rising dyno curves hugging the bottom inside edge. Drawn from a
  // low baseline so they never reach the content area. Torque (blue) flattens;
  // power (red) keeps climbing — a classic dyno silhouette.
  const cx0 = x + 12;
  const cx1 = x2 - 12;
  const baseY = y2 - 8; // baseline near the bottom inner edge
  const span = cx1 - cx0;
  // Power (red): rises further across the whole width.
  const powerY1 = baseY - 4;
  const powerY2 = baseY - 24;
  const powerY3 = baseY - 30;
  const power =
    `M ${cx0} ${powerY1}` +
    ` C ${cx0 + span * 0.4} ${powerY1} ${cx0 + span * 0.5} ${powerY2} ${cx0 + span * 0.72} ${powerY2}` +
    ` S ${cx1 - span * 0.12} ${powerY3} ${cx1} ${powerY3}`;
  // Torque (blue): rises early then flattens, lower than power.
  const torqueY1 = baseY - 2;
  const torqueY2 = baseY - 16;
  const torque =
    `M ${cx0} ${torqueY1}` +
    ` C ${cx0 + span * 0.28} ${torqueY1} ${cx0 + span * 0.34} ${torqueY2} ${cx0 + span * 0.55} ${torqueY2}` +
    ` S ${cx1 - span * 0.18} ${torqueY2 + 2} ${cx1} ${torqueY2 + 3}`;

  return (
    <div style={{ position: 'absolute', top: -BLEED, left: -BLEED, width: W, height: H, pointerEvents: 'none' }}>
      <svg width={W} height={H} xmlns="http://www.w3.org/2000/svg">
        {/* border: soft glow under a crisp red line */}
        <path d={border} fill="none" stroke={RED} strokeWidth={5} opacity={0.12} />
        <path d={border} fill="none" stroke={RED} strokeWidth={1.3} opacity={0.85} />

        {/* RPM tick scale along the top edge (ticks hang up into the bleed) */}
        {ticks.map((t, i) => (
          <line
            key={`t${i}`}
            x1={t.tx}
            y1={y - 1}
            x2={t.tx}
            y2={y - 1 - t.len}
            stroke={t.isRedline ? RED : '#c9b6b8'}
            strokeWidth={t.isRedline ? 1.8 : t.isMajor ? 1.4 : 1}
            opacity={t.isRedline ? 0.95 : t.isMajor ? 0.7 : 0.45}
          />
        ))}

        {/* dim RPM label, top-left in the bleed margin */}
        <text
          x={x + 2}
          y={y - 13}
          fill={RED}
          fontSize={6}
          fontWeight="800"
          letterSpacing={1}
          opacity={0.55}
        >
          RPM x1000
        </text>

        {/* dyno curves hugging the bottom inside edge */}
        <path d={torque} fill="none" stroke={BLUE} strokeWidth={1.3} opacity={0.55} />
        <path d={power} fill="none" stroke={RED} strokeWidth={1.5} opacity={0.65} />
      </svg>
    </div>
  );
}
